// Lightweight telemetry client: captures JS errors, slow/failed API calls, and page loads.
// Fire-and-forget — never throws to caller, never blocks the UI.
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND}/api/telemetry`;
const SLOW_CLIENT_MS = 2000;

// Simple in-memory dedupe to avoid spamming identical events in a burst
const _recent = new Map();
function shouldSend(key) {
  const now = Date.now();
  const last = _recent.get(key) || 0;
  if (now - last < 5000) return false; // 5s cooldown per key
  _recent.set(key, now);
  // Trim old
  if (_recent.size > 200) {
    const cutoff = now - 60000;
    for (const [k, t] of _recent) if (t < cutoff) _recent.delete(k);
  }
  return true;
}

function send(payload) {
  try {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // Use fetch with keepalive so it survives page unloads
    fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {});
  } catch (_) { /* silent */ }
}

function currentRoute() {
  try { return window.location.pathname + window.location.search; } catch { return ''; }
}

export function trackJsError(error, info = {}) {
  const msg = (error && error.message) || String(error) || 'unknown error';
  const stack = (error && error.stack) || info.componentStack || null;
  const key = `jserr:${msg}:${currentRoute()}`;
  if (!shouldSend(key)) return;
  send({
    kind: 'js_error',
    message: msg.slice(0, 500),
    stack: stack ? String(stack).slice(0, 3000) : null,
    route: currentRoute(),
    user_agent: navigator.userAgent,
    extra: info || null
  });
}

export function trackPageLoad(route, durationMs) {
  if (!route || durationMs < 50) return;
  send({
    kind: 'page_load',
    route,
    duration_ms: Math.round(durationMs),
    user_agent: navigator.userAgent
  });
}

export function installTelemetry() {
  if (window.__telemetryInstalled) return;
  window.__telemetryInstalled = true;

  // 1. Global JS errors
  window.addEventListener('error', (e) => {
    trackJsError(e.error || new Error(e.message), { source: e.filename, line: e.lineno });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    trackJsError(reason instanceof Error ? reason : new Error(String(reason)), { type: 'unhandledrejection' });
  });

  // 2. Axios interceptor — capture slow/failed API calls
  axios.interceptors.request.use((config) => {
    config.__startedAt = performance.now();
    return config;
  });
  axios.interceptors.response.use(
    (response) => {
      try {
        const start = response.config.__startedAt;
        if (start) {
          const ms = performance.now() - start;
          if (ms >= SLOW_CLIENT_MS) {
            const url = (response.config.url || '').replace(BACKEND, '');
            const key = `slow:${response.config.method}:${url}`;
            if (shouldSend(key)) {
              send({
                kind: 'api_slow_client',
                path: url,
                method: (response.config.method || 'GET').toUpperCase(),
                status_code: response.status,
                duration_ms: Math.round(ms),
                route: currentRoute()
              });
            }
          }
        }
      } catch (_) {}
      return response;
    },
    (error) => {
      try {
        const cfg = error.config || {};
        const start = cfg.__startedAt;
        const ms = start ? performance.now() - start : null;
        const url = (cfg.url || '').replace(BACKEND, '');
        const status = error.response?.status || 0;

        // Filter out noise: 401s on auth endpoints + auth probes during navigation
        // are normal behavior (session expiry, page transitions), not real bugs.
        const isAuthNoise = status === 401 && (
          url.endsWith('/api/auth/me') ||
          url.endsWith('/api/auth/logout') ||
          url.endsWith('/api/auth/refresh')
        );
        // Also filter ANY 401 on first page load before token is restored
        // (typically when a page mounts and token is still null)
        const hasNoToken = !localStorage.getItem('token');
        const isUnauthedRequest = status === 401 && hasNoToken;

        if (isAuthNoise || isUnauthedRequest) {
          return Promise.reject(error);
        }

        const key = `err:${cfg.method}:${url}:${status}`;
        if (shouldSend(key)) {
          send({
            kind: 'api_error_client',
            path: url,
            method: (cfg.method || 'GET').toUpperCase(),
            status_code: status,
            duration_ms: ms ? Math.round(ms) : null,
            message: error.message,
            route: currentRoute()
          });
        }
      } catch (_) {}
      return Promise.reject(error);
    }
  );

  // 3. Page load timing via PerformanceObserver
  try {
    if (window.performance && window.performance.getEntriesByType) {
      setTimeout(() => {
        const nav = window.performance.getEntriesByType('navigation')[0];
        if (nav && nav.duration) {
          trackPageLoad(currentRoute(), nav.duration);
        }
      }, 2000);
    }
  } catch (_) {}
}
